/**
 * Per-workflow card on the Post-Completion tab.
 *
 * Owns:
 *   - Local draft state for status / submission_ref / notes / deadline
 *   - Documents list (fetch + upload + download) for this item
 *   - Cover-sheet generation (SDLT / AP1 only)
 *   - WebFiling deep-link surfacing (MR01 only — pulled from the most
 *     recent `submitted` event's raw payload)
 *   - The kind-aware SubmissionFormDialog that actually collects the
 *     statutory data when the solicitor clicks "Submit to {provider}"
 *
 * Extracted from PostCompletionTab.jsx (Feb 2026 refactor) to keep
 * the parent tab focused on layout + load orchestration.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import API from "@/lib/api";
import SubmissionFormDialog from "@/components/app/SubmissionFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Stamp,
  Bank,
  PaperPlaneTilt,
  ArrowsClockwise,
  Trash,
  Paperclip,
  FileText,
} from "@phosphor-icons/react";
import { KIND_META, STATUS_OPTIONS } from "./constants";
import StatusPill from "./StatusPill";
import DeadlinePill from "./DeadlinePill";

const EMPTY_DOC_HINTS = {
  mr01: "Drop the MR04 certificate from Companies House here once you've filed via WebFiling.",
  sdlt: "Attach the SDLT5 certificate once HMRC return it.",
  ap1: "Attach the HMLR completion notice / TID once registration completes.",
};

export default function ItemCard({ caseId, caseData, item, onChange, onDelete }) {
  const meta = KIND_META[item.kind] || {
    label: item.kind,
    provider: item.provider_label,
    icon: Stamp,
    color: "text-slate-700",
  };
  const Icon = meta.icon;
  const [draft, setDraft] = useState({
    status: item.status,
    submission_ref: item.submission_ref || "",
    notes: item.notes || "",
    deadline_date: item.deadline_date || "",
  });
  const [saving, setSaving] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);
  const [generatingPrint, setGeneratingPrint] = useState(false);
  const [downloadingBundle, setDownloadingBundle] = useState(false);
  const [polling, setPolling] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenCert, setRegenCert] = useState("");
  const [regenIssue, setRegenIssue] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  // Fetch attached docs once per item.
  useEffect(() => {
    let cancelled = false;
    API.get(`/cases/${caseId}/post-completion/items/${item.id}/documents`)
      .then(({ data }) => { if (!cancelled) setDocs(data.documents || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [caseId, item.id]);

  // Re-sync local edits whenever the parent pushes a fresh item.
  useEffect(() => {
    setDraft({
      status: item.status,
      submission_ref: item.submission_ref || "",
      notes: item.notes || "",
      deadline_date: item.deadline_date || "",
    });
  }, [item.id, item.status, item.submission_ref, item.notes, item.deadline_date]);

  const dirty =
    draft.status !== item.status ||
    (draft.submission_ref || "") !== (item.submission_ref || "") ||
    (draft.notes || "") !== (item.notes || "") ||
    (draft.deadline_date || "") !== (item.deadline_date || "");

  const supportsCoverSheet = item.kind === "sdlt" || item.kind === "ap1";

  // MR01-only: the CH adapter returns a WebFiling deep-link in its
  // submit response — find the most recent one and expose as a button.
  const webfilingUrl = useMemo(() => {
    if (item.kind !== "mr01") return null;
    const events = item.events || [];
    for (let i = events.length - 1; i >= 0; i--) {
      const url = events[i]?.metadata?.raw?.webfiling_url;
      if (url) return url;
    }
    return null;
  }, [item]);

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await API.patch(
        `/cases/${caseId}/post-completion/items/${item.id}`,
        draft,
      );
      onChange(data);
      toast.success(`${meta.label} updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove this ${meta.label} workflow from the case?`)) return;
    try {
      await API.delete(`/cases/${caseId}/post-completion/items/${item.id}`);
      onDelete(item.id);
      toast.success("Item removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const tid = toast.loading(`Uploading ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post(
        `/cases/${caseId}/post-completion/items/${item.id}/documents`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setDocs((d) => [data, ...d]);
      toast.success(`Attached ${file.name}`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed", { id: tid });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const resp = await API.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: doc.content_type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleGenerateCoverSheet = async () => {
    setGeneratingCover(true);
    const tid = toast.loading(
      item.kind === "sdlt" ? "Generating SDLT5 cover sheet…" : "Generating AP1 cover letter…",
    );
    try {
      const { data } = await API.post(
        `/cases/${caseId}/post-completion/items/${item.id}/cover-sheet`,
      );
      setDocs((d) => [data, ...d.filter((x) => x.id !== data.id)]);
      await handleDownload(data);
      toast.success(`Generated ${data.original_filename}`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cover sheet generation failed", { id: tid });
    } finally {
      setGeneratingCover(false);
    }
  };

  // Print-ready replica of the statutory paper form using the captured
  // metadata (SDLT1 / AP1 / MR01). The endpoint returns the PDF directly
  // and also auto-files a copy into the case Documents folder.
  const handleGeneratePrintForm = async () => {
    setGeneratingPrint(true);
    const tid = toast.loading(`Building ${item.kind.toUpperCase()} print copy…`);
    try {
      const resp = await API.post(
        `/cases/${caseId}/post-completion/items/${item.id}/printable-form`,
        {},
        { responseType: "blob" },
      );
      const filename = `${item.kind.toUpperCase()}-print.pdf`;
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Generated — also filed under Documents`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Print form generation failed", { id: tid });
    } finally {
      setGeneratingPrint(false);
    }
  };

  // One-tap ZIP of the three filed statutory SDLT PDFs (SDLT1 + SDLT5 +
  // submission receipt) for the HMLR AP1 application.
  const handleDownloadBundle = async () => {
    setDownloadingBundle(true);
    const tid = toast.loading("Building SDLT bundle…");
    try {
      const resp = await API.get(
        `/cases/${caseId}/post-completion/items/${item.id}/sdlt-bundle`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = "SDLT-bundle.zip"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("SDLT bundle downloaded (SDLT1 + SDLT5 + receipt)", { id: tid });
    } catch (err) {
      const msg = err.response?.status === 404
        ? "No SDLT documents filed yet — submit the return first."
        : (err.response?.data?.detail || "Bundle download failed");
      toast.error(msg, { id: tid });
    } finally {
      setDownloadingBundle(false);
    }
  };

  const hasSdltBundle = item.kind === "sdlt" && docs.some((d) => d.provider_source === "hmrc-sdlt");

  // SDLT: re-poll HMRC on the stored CorrelationID to retrieve the
  // authoritative UTRN + SDLT5 when the submit response came back as an
  // async acknowledgement (final response arrives after our poll window).
  const handlePollStatus = async () => {
    setPolling(true);
    const tid = toast.loading("Checking HMRC for the UTRN / SDLT5…");
    try {
      const { data } = await API.post(
        `/cases/${caseId}/post-completion/items/${item.id}/poll-status`,
      );
      onChange(data);
      API.get(`/cases/${caseId}/post-completion/items/${item.id}/documents`)
        .then(({ data: d }) => setDocs(d.documents || []))
        .catch(() => {});
      if (data.returned_utrn) {
        toast.success(`UTRN ${data.returned_utrn} — certificate & receipt refreshed`, { id: tid });
      } else if (data.status === "completed") {
        toast.success("HMRC confirmed the return is complete.", { id: tid });
      } else {
        toast.info("HMRC is still processing — try again shortly.", { id: tid });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status check failed", { id: tid });
    } finally {
      setPolling(false);
    }
  };

  const showPollButton = item.kind === "sdlt" && !!item.submission_ref;

  const handleRegenerate = async () => {
    setRegenerating(true);
    const tid = toast.loading("Regenerating SDLT1, SDLT5 & receipt…");
    try {
      const payload = {};
      if (regenCert.trim()) payload.certificate_number = regenCert.trim();
      if (regenIssue.trim()) payload.issue_date = regenIssue.trim();
      const { data } = await API.post(
        `/cases/${caseId}/post-completion/items/${item.id}/regenerate-documents`,
        payload,
      );
      onChange(data);
      API.get(`/cases/${caseId}/post-completion/items/${item.id}/documents`)
        .then(({ data: d }) => setDocs(d.documents || []))
        .catch(() => {});
      toast.success("Documents regenerated and re-filed", { id: tid });
      setRegenOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Regeneration failed", { id: tid });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid={`pc-card-${item.kind}`}>
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-slate-50 ${meta.color}`}>
            <Icon weight="duotone" className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-base text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              {meta.label}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">{meta.provider}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">{meta.deadlineCopy}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <StatusPill status={item.status} />
          <DeadlinePill deadline={item.deadline_date} status={item.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {item.kind === "sdlt" && (item.returned_utrn || item.sdlt5_certificate_number) && (
          <div
            className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"
            data-testid="pc-sdlt-utrn-banner"
          >
            {item.returned_utrn && (
              <div className="text-xs">
                <span className="uppercase tracking-wide text-emerald-700/70 font-semibold mr-1.5">UTRN</span>
                <span className="font-mono font-bold text-emerald-900" data-testid="pc-sdlt-utrn-value">{item.returned_utrn}</span>
              </div>
            )}
            {item.sdlt5_certificate_number && (
              <div className="text-xs">
                <span className="uppercase tracking-wide text-emerald-700/70 font-semibold mr-1.5">SDLT5</span>
                <span className="font-mono font-bold text-emerald-900">{item.sdlt5_certificate_number}</span>
              </div>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Status</Label>
            <Select value={draft.status} onValueChange={(v) => setDraft((d) => ({ ...d, status: v }))}>
              <SelectTrigger className="h-9 text-sm" data-testid={`pc-status-select-${item.kind}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Submission ref</Label>
            <Input
              className="h-9 text-sm font-mono"
              value={draft.submission_ref}
              onChange={(e) => setDraft((d) => ({ ...d, submission_ref: e.target.value }))}
              placeholder="—"
              data-testid={`pc-ref-input-${item.kind}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Deadline</Label>
            <Input
              type="date"
              className="h-9 text-sm"
              value={draft.deadline_date}
              onChange={(e) => setDraft((d) => ({ ...d, deadline_date: e.target.value }))}
              data-testid={`pc-deadline-input-${item.kind}`}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Notes</Label>
          <Textarea
            rows={2}
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            placeholder="Add any context — e.g. waiting on completion statement, requisition reference, etc."
            className="text-sm"
            data-testid={`pc-notes-${item.kind}`}
          />
        </div>

        {/* Attached documents */}
        <div className="border-t border-slate-100 pt-3 mt-2" data-testid={`pc-docs-${item.kind}`}>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">
              Attached documents {docs.length > 0 && <span className="text-slate-700 font-bold">({docs.length})</span>}
            </Label>
            <label className="cursor-pointer">
              <input
                type="file"
                onChange={handleUpload}
                disabled={uploading}
                className="hidden"
                data-testid={`pc-upload-input-${item.kind}`}
              />
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-[#1462a0] hover:bg-[#1462a0]/10 rounded transition-colors">
                <Paperclip weight="bold" className="w-3 h-3" />
                {uploading ? "Uploading…" : "Attach file"}
              </span>
            </label>
          </div>
          {docs.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic">
              {EMPTY_DOC_HINTS[item.kind] || "No documents attached yet."}
            </p>
          ) : (
            <div className="space-y-1">
              {docs.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => handleDownload(d)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded bg-slate-50 hover:bg-slate-100 transition-colors text-xs text-left"
                  data-testid={`pc-doc-${d.id}`}
                >
                  <Paperclip weight="bold" className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="flex-1 text-slate-900 font-medium truncate">{d.original_filename}</span>
                  {d.provider_source && d.provider_source !== "manual_upload" && (
                    <span className="text-[10px] uppercase tracking-wide font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                      from {d.provider_source}
                    </span>
                  )}
                  <span className="text-[10px] text-slate-400 shrink-0">{new Date(d.created_at).toLocaleDateString("en-GB")}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            className="bg-[#1462a0] hover:bg-[#10507f] text-white"
            data-testid={`pc-save-${item.kind}`}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
          {item.status !== "completed" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSubmitDialogOpen(true)}
              data-testid={`pc-submit-${item.kind}`}
            >
              <PaperPlaneTilt weight="bold" className="w-3.5 h-3.5 mr-1" />
              Submit to {meta.provider.split(" ")[0]}
            </Button>
          )}
          {showPollButton && (
            <Button
              size="sm"
              variant="outline"
              onClick={handlePollStatus}
              disabled={polling}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="pc-poll-status-sdlt"
            >
              <ArrowsClockwise weight="bold" className={`w-3.5 h-3.5 mr-1 ${polling ? "animate-spin" : ""}`} />
              {polling ? "Checking HMRC…" : "Check HMRC status"}
            </Button>
          )}
          {item.kind === "sdlt" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setRegenCert(item.sdlt5_certificate_number || ""); setRegenIssue(item.sdlt5_issue_date || ""); setRegenOpen(true); }}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="pc-regenerate-docs-sdlt"
            >
              <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1" />
              Regenerate docs
            </Button>
          )}
          {supportsCoverSheet && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleGenerateCoverSheet}
              disabled={generatingCover}
              className="border-[#1462a0]/40 text-[#1462a0] hover:bg-[#1462a0]/10"
              data-testid={`pc-cover-${item.kind}`}
            >
              <FileText weight="bold" className="w-3.5 h-3.5 mr-1" />
              {generatingCover ? "Generating…" : item.kind === "sdlt" ? "Generate SDLT5 cover" : "Generate AP1 letter"}
            </Button>
          )}
          {/* Print-ready replica of the underlying statutory paper form. */}
          {["sdlt", "ap1", "mr01"].includes(item.kind) && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleGeneratePrintForm}
              disabled={generatingPrint}
              className="border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid={`pc-print-${item.kind}`}
            >
              <FileText weight="bold" className="w-3.5 h-3.5 mr-1" />
              {generatingPrint ? "Building…" : `Print ${item.kind.toUpperCase()} form`}
            </Button>
          )}
          {hasSdltBundle && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadBundle}
              disabled={downloadingBundle}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="pc-sdlt-bundle-download"
            >
              <FileText weight="bold" className="w-3.5 h-3.5 mr-1" />
              {downloadingBundle ? "Preparing…" : "Download SDLT bundle"}
            </Button>
          )}
          {webfilingUrl && (
            <Button
              size="sm"
              variant="outline"
              asChild
              className="border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid="pc-webfiling-link"
            >
              <a href={webfilingUrl} target="_blank" rel="noopener noreferrer">
                <Bank weight="bold" className="w-3.5 h-3.5 mr-1" />
                Open WebFiling
              </a>
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowLog((v) => !v)}
            data-testid={`pc-toggle-log-${item.kind}`}
          >
            <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1" />
            {showLog ? "Hide log" : `Activity (${(item.events || []).length})`}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDelete}
            className="text-rose-600 hover:text-rose-700 ml-auto"
            data-testid={`pc-delete-${item.kind}`}
          >
            <Trash weight="bold" className="w-3.5 h-3.5" />
          </Button>
        </div>
        {showLog && (
          <div className="border-t border-slate-100 pt-3 mt-2 space-y-1.5 text-xs" data-testid={`pc-log-${item.kind}`}>
            {(item.events || []).length === 0 ? (
              <p className="text-slate-400 italic">No activity yet.</p>
            ) : (
              [...(item.events || [])].reverse().map((ev, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-slate-400 w-32 shrink-0">
                    {new Date(ev.ts).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                  <span className="text-slate-700">
                    <span className="font-medium text-slate-900">{ev.user_name || "System"}</span>
                    {" — "}
                    {ev.message}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
      <SubmissionFormDialog
        open={submitDialogOpen}
        onOpenChange={setSubmitDialogOpen}
        caseId={caseId}
        caseData={caseData}
        item={item}
        onSubmitted={(updated) => {
          onChange(updated);
          API.get(`/cases/${caseId}/post-completion/items/${item.id}/documents`)
            .then(({ data }) => setDocs(data.documents || []))
            .catch(() => {});
        }}
      />
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="sm:max-w-md" data-testid="pc-regenerate-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Regenerate SDLT documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500 leading-snug">
              Re-renders the SDLT1, SDLT5 and submission receipt from the current case data and replaces the previous copies. HMRC issues the <strong>certificate number</strong> (the IRmark) and the <strong>date/time of issue</strong> on its own SDLT5 — if you have them from HMRC, enter them below so your copy matches exactly. Leave blank to use what's on file.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Certificate number (IRmark) — optional</Label>
              <Input value={regenCert} onChange={(e) => setRegenCert(e.target.value)} placeholder="e.g. 5SOE5FBJSKUAXTMWNBUN2HIPIF6TEYTB" data-testid="pc-regen-cert-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Date &amp; time of issue — optional</Label>
              <Input type="datetime-local" value={(regenIssue || "").slice(0, 16)} onChange={(e) => setRegenIssue(e.target.value)} data-testid="pc-regen-issue-input" />
              <p className="text-[11px] text-slate-400">As shown on HMRC's SDLT5 (e.g. 13/10/2025 10:36:40).</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenOpen(false)}>Cancel</Button>
            <Button onClick={handleRegenerate} disabled={regenerating} className="text-white" style={{ background: "#100f5d" }} data-testid="pc-regen-confirm-btn">
              {regenerating ? "Regenerating…" : "Regenerate & re-file"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
